/**
 * Navbar Module
 * Handles navbar behavior including scroll effects and mobile menu
 */

import { $, on, smoothScroll, getOffsetTop } from '../utils/dom.js';
import { CONFIG, SELECTORS } from '../config/constants.js';

export class Navbar {
    constructor() {
        this.navbar = $(SELECTORS.NAVBAR);
        this.logo = $(SELECTORS.LOGO);
        this.navLinks = document.querySelectorAll(SELECTORS.NAV_LINKS);
        this.hamburger = $('.hamburger');
        this.navLinksContainer = $('.nav-links');
        this.mobileOverlay = $('.mobile-menu-overlay');
        this.isMenuOpen = false;

        if (!this.navbar) {
            console.warn('Navbar element not found');
            return;
        }

        this.init();
    }

    init() {
        this.setupScrollEffect();
        this.setupLogoClick();
        this.setupSmoothScroll();
        this.setupMobileMenu();
    }

    /**
     * Setup navbar glassmorphism scroll effect
     */
    setupScrollEffect() {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const navbarStyles = window.getComputedStyle(this.navbar);
            const navbarPosition = navbarStyles.position;
            const navbarDisplay = navbarStyles.display;
            const navbarVisibility = navbarStyles.visibility;
            const navbarOpacity = navbarStyles.opacity;
            const navbarTop = this.navbar.getBoundingClientRect().top;

            const navbarZIndex = navbarStyles.zIndex;
            const navbarHeight = this.navbar.offsetHeight;
            const navbarBounds = this.navbar.getBoundingClientRect();

            // Check what element is at the navbar's center point
            const centerX = navbarBounds.left + (navbarBounds.width / 2);
            const centerY = navbarBounds.top + (navbarBounds.height / 2);
            const elementAtCenter = document.elementFromPoint(centerX, centerY);
            const elementAtCenterTag = elementAtCenter ? elementAtCenter.tagName : 'null';
            const elementAtCenterClass = elementAtCenter ? elementAtCenter.className : 'null';

            console.log('🔍 NAVBAR SCROLL DEBUG:', {
                scrollY,
                navbarPosition,
                navbarDisplay,
                navbarVisibility,
                navbarOpacity,
                navbarZIndex,
                navbarTop,
                navbarHeight,
                navbarBounds: {
                    top: navbarBounds.top,
                    bottom: navbarBounds.bottom,
                    left: navbarBounds.left,
                    right: navbarBounds.right,
                    width: navbarBounds.width,
                    height: navbarBounds.height
                },
                elementAtNavbarCenter: {
                    tag: elementAtCenterTag,
                    class: elementAtCenterClass,
                    isNavbar: elementAtCenter === this.navbar || this.navbar.contains(elementAtCenter)
                },
                classList: Array.from(this.navbar.classList),
                windowWidth: window.innerWidth
            });

            if (window.scrollY > 20) {
                this.navbar.classList.add('scrolled');
                console.log('✅ Added "scrolled" class');
            } else {
                this.navbar.classList.remove('scrolled');
                console.log('❌ Removed "scrolled" class');
            }
        };

        on(window, 'scroll', handleScroll);

        // Log initial state
        console.log('🚀 Navbar initialized:', {
            navbar: this.navbar,
            initialPosition: window.getComputedStyle(this.navbar).position,
            initialDisplay: window.getComputedStyle(this.navbar).display,
            windowWidth: window.innerWidth
        });
    }

    /**
     * Setup logo click to scroll to top
     */
    setupLogoClick() {
        if (!this.logo) return;

        on(this.logo, 'click', (e) => {
            const isHomePage = window.location.pathname.includes('index.html') ||
                             window.location.pathname === '/';

            if (isHomePage) {
                e.preventDefault();
                smoothScroll(0);
                this.closeMobileMenu();
            }
        });
    }

    /**
     * Setup smooth scroll for navigation links
     */
    setupSmoothScroll() {
        this.navLinks.forEach(link => {
            const href = link.getAttribute('href');

            // Only handle internal anchor links
            if (href && href.startsWith('#') && href !== '#') {
                on(link, 'click', (e) => {
                    e.preventDefault();
                    const targetElement = $(href);

                    if (targetElement) {
                        const navbarHeight = this.navbar.offsetHeight;
                        const targetPosition = getOffsetTop(targetElement) - navbarHeight;
                        smoothScroll(targetPosition);
                        this.closeMobileMenu();
                    }
                });
            }
        });
    }

    /**
     * Setup mobile hamburger menu
     */
    setupMobileMenu() {
        if (!this.hamburger || !this.navLinksContainer) {
            console.error('Missing hamburger or nav links container!');
            return;
        }

        // Hamburger click
        on(this.hamburger, 'click', (e) => {
            e.stopPropagation();
            this.toggleMobileMenu();
        });

        // Overlay click to close menu
        if (this.mobileOverlay) {
            on(this.mobileOverlay, 'click', () => {
                this.closeMobileMenu();
            });
        }

        // Close menu on escape key
        on(document, 'keydown', (e) => {
            if (e.key === 'Escape' && this.isMenuOpen) {
                this.closeMobileMenu();
            }
        });

        // Close menu on window resize if switching to desktop
        on(window, 'resize', () => {
            if (window.innerWidth > 768 && this.isMenuOpen) {
                this.closeMobileMenu();
            }
        });
    }

    /**
     * Toggle mobile menu open/close
     */
    toggleMobileMenu() {
        if (this.isMenuOpen) {
            this.closeMobileMenu();
        } else {
            this.openMobileMenu();
        }
    }

    /**
     * Open mobile menu
     */
    openMobileMenu() {
        this.isMenuOpen = true;
        this.hamburger.classList.add('active');
        this.navLinksContainer.classList.add('active');
        if (this.mobileOverlay) {
            this.mobileOverlay.classList.add('active');
        }
        document.body.classList.add('menu-open');
    }

    /**
     * Close mobile menu
     */
    closeMobileMenu() {
        this.isMenuOpen = false;
        this.hamburger.classList.remove('active');
        this.navLinksContainer.classList.remove('active');
        if (this.mobileOverlay) {
            this.mobileOverlay.classList.remove('active');
        }
        document.body.classList.remove('menu-open');
    }
}
